import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {cloneDeep, inRange} from 'lodash';
import * as moment from "moment";

export class TValidators extends Validators {

  static link(control: AbstractControl): ValidationErrors {
    const regex =
      /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g;
    return regex.test(control.value && control.value.trim()) ? {} : {
      link: true,
    };
  }

  static numberAndString(control: AbstractControl): ValidationErrors {
    const regex = /[a-zA-Z0-9()]/g;
    return regex.test(control.value && control.value.trim()) ? {} : {
      numberAndString: true,
    };
  }

  static textRange =
    (min: number, max: number) => (control: AbstractControl) => {
      const value = control.value && control.value.toString().trim();
      if (value && value.length >= min && value.length <= max) {
        return null;
      }
      return {
        textRange: true,
      };
    };

  static numberRange =
    (min?: number, max?: number, result: any = {
      numberRange: {
        min: min,
        max: max,
      }
    }) => (control: AbstractControl) => {

      if (control.value) {
        const value = Number(control.value.toString().trim());
        if (( min || 0) <= value && value <= (max || Infinity + 0.001)) return null;
        return result;
      }
      return null;
    };

  static emailRules(result: any = { emailRules: true} ): ValidationErrors {
    return (control: FormGroup) => {
      const value = control.value && control.value.trim();
      const regex = /^[a-zA-Z0-9]+([._\-+][a-zA-Z0-9]+)*@([\w-]+\.)+[\w-]{2,4}$/;
      // const regexDupSpecialChar = /\.\.|--|__/;
      if (value && (!regex.test(value))) {
        return result;
      }
      return null;
    }

  }

  static phoneNumber(result: any = { phoneNumber: true }): ValidationErrors {
    return (control: FormGroup) => {
      const value = control.value && control.value.trim();
      const regex = value?.startsWith('02') ? /^([0])+([0-9]{10})$/ : /^([0])+([0-9]{9})$/;
      if (value && !regex.test(value)) {
        return result;
      }
      return null;
    }
  }
  static error(message: string) {
    return (control: AbstractControl) => {
      if(message) {
        return { customErrorText: '', message };
      }
      return null;
    };
  }

  static required(result: any = { required: true }) {
    return (control: AbstractControl) => {
      if (control.value === null || control.value === undefined || ((typeof control.value == 'string' || Array.isArray(control.value)) && !this.trimData(control.value)?.length)) {
        return result;
      }
      return null;
    };
  }

  static pattern(regex: any, message: string = "Invalid value", result: any = { pattern: true, message: message}) {
    return (control: AbstractControl) => {
      const value = control.value;
      const regexTest = cloneDeep(regex);
      if (value && !regexTest.test(value)) {
        return result;
      }
      return null;
    };
  }

  static MinLength(min: number, result: any = { min: true }) {
    return (control: FormGroup) => {
      if (control.value && control.value.length < min) {
        return result;
      }
      return null;

    };
  }

  static MaxLength(max: number, result: any = { max: true }) {
    return (control: FormGroup) => {
      if (control.value && control.value.length > max) {
        return result;
      }
      return null;

    };
  }

  static MinLengthNumber(min: number, result: any = { min: true }) {
    return (control: FormGroup) => {
      if (!isNaN(control.value) && parseFloat(control.value) < min) {
        return result;
      }
      return null;

    };
  }

  static MaxLengthNumber(max: number, result: any = { max: true }) {
    return (control: FormGroup) => {
      if (control.value && parseFloat(control.value) > max) {
        return result;
      }
      return null;

    };
  }

  static trimData(data: any): any {
    if (data && typeof data === 'string') {
      return data.trim();
    }
    return data;
  }


  static timeValidator =
    (startField: string, endField: string, result: any = { endBeforeStart: true }) => (formGroup: AbstractControl) => {
      let startDate = formGroup.value[startField];
      let endDate = formGroup.value[endField];
      if (!startDate || !endDate) {
        return;
      }
      startDate = new Date(startDate).getTime();
      endDate = new Date(endDate).getTime();
      return endDate < startDate ? result : null;
    };

  static dateRangeValidator = (startField: string, endField: string, message:string): ValidatorFn =>{
    return(control: AbstractControl)=>{
      if(!control.value){
        return null
      }
      let startDate = control?.parent?.value[startField];
      let endDate = control?.parent?.value[endField];
      if (!startDate || !endDate) {
        return null
      }
      startDate = new Date(startDate)
      endDate = new Date(endDate)
      if(startDate<=endDate){
        return null
      }
      else{
        return {dateRangeValidator: message || `Only allowed between ${startDate.toLocaleDateString()} and ${endDate.toLocaleDateString()}`}
      }
    }
  }

  static dateWithFieldValidator = (field: string, operator: ('gt'| 'gte' | 'lt' | 'lte') = 'gt',message:string): ValidatorFn =>{
    return (control: AbstractControl): ValidationErrors | null => {
      let compareDate = control?.parent?.value[field];
      if (!control.value || !compareDate) {
        return null;
      }
      compareDate = new Date(compareDate)
      const startDate = new Date(control.value).getTime();
      return (
        (operator == 'gt' && startDate < compareDate.getTime()) ||
        (operator == 'gte' && startDate <= compareDate.getTime()) ||
        (operator == 'lt' && startDate > compareDate.getTime()) ||
        (operator == 'lte' && startDate >= compareDate.getTime())
      ) ? { dateValidator: message || true } : null;
    }
  }

    static excludesDateValidator = (dates: Date[], message: string = '', format = 'DD/MM/YYYY'): ValidatorFn => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
          return null;
        }
        const val = moment(new Date(control.value)).format(format);
        return dates?.some(d => moment(d).format(format) == val) ? { dateValidator: message || true } : null;
      }
    };

    static dateValidator = (compareDate: Date, message: string = ''): ValidatorFn => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value || !compareDate) {
          return null;
        }
        const startDate = new Date(control.value).getTime();
        return startDate < compareDate.getTime() ? { dateValidator: message || true } : null;
      }
    };

    static maxDateValidator = (compareDate: Date,message: string = ''): ValidatorFn => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value || !compareDate) {
          return null;
        }
        const startDate = new Date(control.value).getTime();

        return startDate > compareDate.getTime() ? { maxDateValidator: message || true } : null;
      }
    };

    static editorMaxLength(maxlength: number): ValidatorFn {
      return (control: AbstractControl) => {
        var regex = /(<([^>]+)>)/ig;
        var value = control.value;
        if (value == null) {
          return null;
        }
        var result = value.replace(regex, "");
        return result.length > maxlength ? { editorMaxLength: { editorMaxLength: true, maxLength: maxlength, actualLength: result.length } } : null;
      }
    }

    static patternReg(pattern: RegExp, result: any = { pattern: true }) {
      return (control: FormGroup) => {
        const value = control.value && control.value.trim();
        if (value && !pattern.test(value)) {
          return result;
        }
        return null;
      };
    }
}
